import axiosInstance from 'services/axiosInstance';
import { IResponse } from 'services/types';
import {
  ICallScribeAccessInfo,
  ICallScribeCallEmotion,
  ICallScribeCallListItem,
  ICallScribeCallTranscript,
  ICallScribeParticipant,
  ICallScribeTimeEntryLink,
  ICallScribeTimeEntryOptions,
  ICallScribeTimeEntryRequest,
  ICallScribeTranscriptSegment,
  ICallScribeVoiceToken,
  IInitiateCallRequest,
  ISentimentFeedback,
  IUpsertSentimentFeedbackRequest,
} from 'services/callScribe/types';

/** GET /api/call-scribe/calls/access — tier-gating probe for the Pod sidebar. */
export const getCallScribeAccess = (): IResponse<ICallScribeAccessInfo> =>
  axiosInstance.request({ method: 'GET', url: 'call-scribe/calls/access' });

/** GET /api/call-scribe/calls/voice-token — short-lived Twilio Voice JWT for the browser SDK.
 *  Skips the global error snackbar; the softphone surfaces unavailability inline. */
export const getCallScribeVoiceToken = (): IResponse<ICallScribeVoiceToken> =>
  axiosInstance.request({
    method: 'GET',
    url: 'call-scribe/calls/voice-token',
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** GET /api/call-scribe/calls/by-ticket — call log for a single PSA ticket. */
export const getCallScribeCallsByTicket = (
  crmSourceId: number,
  ticketId: string,
): IResponse<ICallScribeCallListItem[]> =>
  axiosInstance.request({
    method: 'GET',
    url: 'call-scribe/calls/by-ticket',
    params: { crmSourceId, ticketId },
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** GET /api/call-scribe/calls/by-contact — call log for a PSA contact, with no ticket. */
export const getCallScribeCallsByContact = (
  crmSourceId: number,
  contactId: string,
  companyId?: string | null,
): IResponse<ICallScribeCallListItem[]> =>
  axiosInstance.request({
    method: 'GET',
    url: 'call-scribe/calls/by-contact',
    params: companyId ? { crmSourceId, contactId, companyId } : { crmSourceId, contactId },
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** GET /api/call-scribe/calls/{callId} — single-call projection (for the popup post-call review). */
export const getCallScribeCall = (callId: number): IResponse<ICallScribeCallListItem> =>
  axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/${callId}`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** POST /api/call-scribe/calls — creates the persisted CallScribeCall and dials Twilio. */
export const initiateCallScribeCall = (
  body: IInitiateCallRequest,
): IResponse<ICallScribeCallListItem> =>
  axiosInstance.request({
    method: 'POST',
    url: 'call-scribe/calls',
    data: body,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });

export const getCallScribeTimeEntryOptions = (
  crmSourceId: number,
  callId?: number,
): IResponse<ICallScribeTimeEntryOptions> =>
  axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/time-entry-options?crmSourceId=${crmSourceId}${
      typeof callId === 'number' ? `&callId=${callId}` : ''
    }`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });

/** POST /api/call-scribe/calls/{callId}/time-entry — pushes the suggested time entry to the PSA. */
export const postCallScribeTimeEntry = (
  callId: number,
  body: ICallScribeTimeEntryRequest,
): IResponse<ICallScribeTimeEntryLink> =>
  axiosInstance.request({
    method: 'POST',
    url: `call-scribe/calls/${callId}/time-entry`,
    data: body,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });

export const stopCallScribeRecording = (
  callId: number,
): IResponse<ICallScribeCallListItem> =>
  axiosInstance.request({
    method: 'POST',
    url: `call-scribe/calls/${callId}/recording/stop`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });

/**
 * Outcome of a draft save, mirroring `CallScribeDraftOutcome` on the BE.
 *
 * The indicator is driven by what the server did, never by the request having been
 * sent: a green check over a ticket that did not change is the defect this endpoint
 * exists to prevent.
 */
export enum CallScribeDraftOutcome {
  SavedLocally = 0,
  SavedAndUpdatedTicket = 1,
  SavedButTicketUpdateUnsupported = 2,
  Failed = 3,
}

export interface ICallScribeDraftResult {
  outcome: CallScribeDraftOutcome;
  message?: string;
  savedAtUtc?: string;
}

/** PATCH /api/call-scribe/calls/{callId}/draft — persists the technician's edits and, where the
 * entry is already posted and the PSA can address it, pushes them to the ticket. */
export const patchCallScribeDraft = (
  callId: number,
  body: { customerNotes?: string; durationMinutes?: number },
): IResponse<ICallScribeDraftResult> =>
  axiosInstance.request({
    method: 'PATCH',
    url: `call-scribe/calls/${callId}/draft`,
    data: body,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });

/** POST /api/call-scribe/calls/{callId}/reject — records a tech rejection of the AI suggestion. */
export const rejectCallScribeSuggestion = (
  callId: number,
): IResponse<ICallScribeTimeEntryLink> =>
  axiosInstance.request({
    method: 'POST',
    url: `call-scribe/calls/${callId}/reject`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });

/** GET /api/call-scribe/calls/{callId}/transcript — most-recent transcript for the call.
 *  Returns 404 until transcription completes; the caller treats 404 as "still processing". */
export const getCallScribeTranscript = (
  callId: number,
): IResponse<ICallScribeCallTranscript> =>
  axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/${callId}/transcript`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** GET /api/call-scribe/calls/{callId}/transcript-segments — committed live-transcript
 *  segments ordered by seq. Empty array when the call predates live transcription. */
export const getCallScribeTranscriptSegments = (
  callId: number,
): IResponse<ICallScribeTranscriptSegment[]> =>
  axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/${callId}/transcript-segments`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

export const getCallScribeParticipants = (
  callId: number,
): IResponse<ICallScribeParticipant[]> =>
  axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/${callId}/participants`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** GET /api/call-scribe/calls/{callId}/empathic-analyses — every emotion-provider row for the
 *  call, sentiment-v2 first. Empty array when the call predates empathic analysis. */
export const getCallScribeEmpathicAnalyses = (
  callId: number,
): IResponse<ICallScribeCallEmotion[]> =>
  axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/${callId}/empathic-analyses`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** GET /api/call-scribe/calls/{callId}/sentiment-feedback — the current user's feedback, or a
 *  null body when they have not given any. */
export const getSentimentFeedback = (
  callId: number,
): IResponse<ISentimentFeedback | null> =>
  axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/${callId}/sentiment-feedback`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** PUT /api/call-scribe/calls/{callId}/sentiment-feedback — upserts the current user's
 *  thumbs-up/down. A foreign or unknown call answers 400 "That call was not found." */
export const putSentimentFeedback = (
  callId: number,
  body: IUpsertSentimentFeedbackRequest,
): IResponse<ISentimentFeedback> =>
  axiosInstance.request({
    method: 'PUT',
    url: `call-scribe/calls/${callId}/sentiment-feedback`,
    data: body,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** DELETE /api/call-scribe/calls/{callId}/sentiment-feedback — clears the current user's
 *  feedback. */
export const deleteSentimentFeedback = (callId: number): IResponse<void> =>
  axiosInstance.request({
    method: 'DELETE',
    url: `call-scribe/calls/${callId}/sentiment-feedback`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    skipErrorSnackbar: true,
  });

/** GET /api/call-scribe/calls/{callId}/recording — streams the recording from blob storage.
 *  Returned as an audio/mpeg blob; the caller wraps it in URL.createObjectURL to feed an
 *  <audio> element. We proxy through the API instead of pointing the browser at the raw
 *  Twilio media URL, which would trigger the OS-level Basic-auth sign-in dialog. */
export const getCallScribeRecording = async (callId: number): Promise<Blob> => {
  const res = await axiosInstance.request({
    method: 'GET',
    url: `call-scribe/calls/${callId}/recording`,
    responseType: 'blob',
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });
  // Some axios wrappers unwrap `.data` in an interceptor and return the body
  // directly; others keep the AxiosResponse shape. Handle both.
  const body = (res as { data?: unknown })?.data ?? res;
  return body as Blob;
};

/**
 * POST /api/call-scribe/calls/{callId}/re-run-intelligence — re-enqueues the
 * post-call transcribe + summarise pipeline for a call that already has a
 * stored recording. Returns 202 immediately; the caller polls
 * `getCallScribeCall(callId)` until `hasIntelligence` flips true.
 */
export const rerunCallScribeIntelligence = (
  callId: number,
): IResponse<{ callId: number; status: string }> =>
  axiosInstance.request({
    method: 'POST',
    url: `call-scribe/calls/${callId}/re-run-intelligence`,
    // @ts-expect-error — custom flag honoured by the global response interceptor.
    rejectWithResponse: true,
  });
